import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { StockExchange } from '../../../models/stockExchange';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-stock-exchange-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './stock-exchange-list.component.html',
  styleUrl: './stock-exchange-list.component.scss'
})
export class StockExchangeListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  stockExchanges = signal<StockExchange[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadStockExchanges();
  }

  loadStockExchanges(): void {
    this.spinnerService.show();
    this.lutService.getStockExchanges().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortStockExchanges(data);
        this.stockExchanges.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading stock exchanges:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortStockExchanges(stockExchanges: StockExchange[]): StockExchange[] {
    return [...stockExchanges].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateStockExchange(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(stockExchange: StockExchange): void {
  }

  onEditStockExchange(stockExchange: StockExchange): void {
  }

  onDeleteStockExchange(stockExchange: StockExchange): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(stockExchange.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalStockExchanges = this.stockExchanges();

          // 1. Optimistically remove from local state immediately
          this.stockExchanges.update(stockExchanges => stockExchanges.filter(s => s.id !== stockExchange.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteStockExchange(stockExchange.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Stock exchange deleted successfully');
              // 3. Refresh cache so other components get updated data
              this.lookupDataService.updateTableData('stockExchanges', this.stockExchanges());
            },
            error: (error) => {
              console.error('Error deleting stock exchange:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.stockExchanges.set(originalStockExchanges);
              this.toast.error(`<strong>Failed to delete Stock Exchange:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: StockExchange, newName: string} | {item: StockExchange, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: StockExchange;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.stockExchanges.update(stockExchanges => {
      const updated = stockExchanges.map(s => s.id === event.item.id ? updatedItem : s);
      return this.sortStockExchanges(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateStockExchange(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Stock exchange updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.stockExchanges.update(stockExchanges => {
          const updated = stockExchanges.map(s => s.id === event.item.id ? response : s);
          return this.sortStockExchanges(updated);
        });
        // 4. Refresh cache so other components get updated data
        this.lookupDataService.updateTableData('stockExchanges', this.stockExchanges());
      },
      error: (error) => {
        console.error('Error updating stock exchange:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.stockExchanges.update(stockExchanges =>
          stockExchanges.map(s => s.id === event.item.id ? originalItem : s)
        );
        this.toast.error(`<strong>Failed to update Stock Exchange:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newStockExchange: StockExchange = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.stockExchanges.update(stockExchanges => this.sortStockExchanges([...stockExchanges, newStockExchange]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createStockExchange({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Stock exchange created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.stockExchanges.update(stockExchanges => {
          const updated = stockExchanges.map(s => s.id === tempId ? response : s);
          return this.sortStockExchanges(updated);
        });
        // 4. Refresh cache so other components get updated data
        this.lookupDataService.updateTableData('stockExchanges', this.stockExchanges());
      },
      error: (error) => {
        console.error('Error creating stock exchange:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.stockExchanges.update(stockExchanges =>
          stockExchanges.filter(s => s.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Stock Exchange:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
